import { Component, OnInit, Input, Output, NgZone, Optional } from '@angular/core';
import { DocumentStatus } from 'app/shared/model/supporting-document';
import { TroveComponent } from 'app/shared/trove.component';
import { MatDialog } from '@angular/material/dialog';
import { LogService } from 'app/services/log.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import * as pdfjsLib from 'pdfjs-dist';
import { DocumentsService } from 'app/services/documents.service';
import { PortalRepository } from 'app/services/repository/portal-repository.service';
import { NotifyRepository } from 'app/services/repository/notify-repository.service';

const maxFileSize = 10485760;
const thumbnailSize = 150; // pixels

@Component({
  selector: 'app-document-selection',
  templateUrl: './document-selection.component.html',
  styleUrls: ['./document-selection.component.css']
})
export class DocumentSelectionComponent extends TroveComponent implements OnInit {

  @Input() index: string;
  @Input() title: string;
  @Input() prompt: string;
  @Input() moreInfoLink: string;
  @Input() nonSelectionButtonText: string;
  @Input() nonSelectionDocumentStatus: DocumentStatus;
  @Input() generateButtonText?: string;
  @Input() url: string;
  @Input() showPhysicalLocation: boolean = false;

  @Output() public filename: string;
  @Output() public data: string;
  @Output() public status?: DocumentStatus;
  @Output() public physicalLocation: string;

  public thumbnailUrl: SafeUrl;
  public fileType: 'image' | 'pdf';
  public fileSize: string;
  public pdfLoadError: string;
  public letterLoading = false;
  private pdfLoadingPromise: Promise<void>;

  public get statusIcon(): string {
    switch (this.status) {
      case 'attached':
        return 'attach_file';
        break;
      case 'wait':
        return 'hourglass_empty';
        break;
      case 'nonexistent':
        return 'clear';
        break;
      default:
        return 'check_box_outline_blank';
        break;
    }
  }

  constructor(
    public dialog: MatDialog,
    private logService: LogService,
    private sanitizer: DomSanitizer,
    @Optional() private notifyRepository: NotifyRepository,
    private documentsService: DocumentsService,
    private ngZone: NgZone
  ) {
    super();
  }
  
  public ngOnInit(): void {
    // Set worker source. Note it was copied into assets using `cp node_modules/pdfjs-dist/build/pdf.worker.min.js src/assets/`
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.js';
  }

  public changeListener($event): void {
    this.logService.log(`changeListener called for component with prompt ${this.prompt}`);
    const files = Array.from($event.target.files) as File[];
    if (files && files[0]) {
      const file = files[0];
      this.logService.log(`Selected file ${file.name} with size ${file.size} and type ${file.type}`);
      
      // Clear previous thumbnail
      this.thumbnailUrl = null;
      this.fileType = null;

      this.loadFileData(file).then(data => {
        if (this.hasAllowedMimeType(data)) {
          this.filename = file.name;
          if (data.length > maxFileSize) {
            this.status = 'too-big';
          } else {
            this.data = data;
            this.url = undefined;
            this.status = 'attached';
            this.generateThumbnail(data);
          }
        } else {
          this.showErrorMessage(this.dialog, 'The selected file was not of the expected type. Please select an image or PDF.');
        }
      });
    }
  }

  public onDelete() {
    this.filename = undefined;
    this.data = undefined;
    this.status = undefined;
    this.url = undefined;
    this.thumbnailUrl = null;
    this.fileType = null;
    this.fileSize = null;
    this.pdfLoadError = null;
    this.pdfLoadingPromise = null;
  }

  public openPdfInNewTab(): void {
    if (this.fileType === 'pdf' && this.data) {
      try {
        // Use the service method to convert base64 to blob
        const blob = this.documentsService.b64toBlob(this.data, 'application/pdf');

        // Open in new tab and check for popup blocking
        if (!this.documentsService.openBlobInNewTab(blob)) {
          this.showMessage(this.dialog, `Pop-up blocked`, `Opening your PDF report may have been blocked by your browser.<br/>Please allow pop-ups from mytrove in your browser's settings.`);
        }
      } catch (error) {
        this.logService.error(`Error opening PDF: ${error}`);
        this.showMessage(this.dialog, 'PROBLEM', 'Could not open the PDF. Please try again');
      }
    }
  }

  public async generateThumbnail(data: string): Promise<void> {
    if (data.startsWith('data:image/')) {
      this.fileType = 'image';
      // For images, create a thumbnail by loading into an image element and drawing on canvas
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate thumbnail dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > thumbnailSize) {
            height = height * (thumbnailSize / width);
            width = thumbnailSize;
          }
        } else {
          if (height > thumbnailSize) {
            width = width * (thumbnailSize / height);
            height = thumbnailSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        this.ngZone.run(() => {
          this.thumbnailUrl = this.sanitizer.bypassSecurityTrustUrl(canvas.toDataURL());
        });
      };
      img.src = data;
    } else if (data.startsWith('data:application/pdf')) {
      this.fileType = 'pdf';
      try {
        // Cancel any existing PDF loading
        if (this.pdfLoadingPromise) {
          this.pdfLoadingPromise = null;
        }

        // Use the service method to convert base64 to blob
        const blob = this.documentsService.b64toBlob(data);
        // Convert blob to array buffer for PDF.js
        const arrayBuffer = await blob.arrayBuffer();
        
        this.pdfLoadingPromise = (async () => {
          // Load the PDF document using array buffer
          const loadingTask = pdfjsLib.getDocument(arrayBuffer);
          const pdf = await loadingTask.promise;

          // Get the first page
          const page = await pdf.getPage(1);
          
          // Calculate scale to fit within thumbnail size while maintaining aspect ratio
          const viewport = page.getViewport({ scale: 1.0 });
          let scale = thumbnailSize / viewport.width;
          if (viewport.height * scale > thumbnailSize) {
            scale = thumbnailSize / viewport.height;
          }
          
          const scaledViewport = page.getViewport({ scale });
          
          // Prepare canvas for rendering
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;
          
          // Render PDF page to canvas
          await page.render({
            canvasContext: context,
            viewport: scaledViewport
          }).promise;
          
          this.ngZone.run(() => {
            this.thumbnailUrl = this.sanitizer.bypassSecurityTrustUrl(canvas.toDataURL());
            this.pdfLoadError = null;
          });
        })();

        await this.pdfLoadingPromise;
      } catch (error) {
        this.logService.error(`Error generating PDF thumbnail: ${error}`);
        this.ngZone.run(() => {
          this.pdfLoadError = 'Could not generate PDF preview';
        });
      }
    }
  }
  public nonSelectionButtonClick() {
    this.filename = undefined;
    this.data = undefined;
    this.url = undefined;
    this.thumbnailUrl = null;
    this.fileType = null;
    this.status = this.nonSelectionDocumentStatus;
    this.logService.log(`Status set to ${this.status} for index ${this.index}`);
  }

  public async generateLetter() {
    this.logService.log('generateLetter');
    if (this.letterLoading || !this.notifyRepository) {
      return;
    }
    try {
      this.letterLoading = true;
      const deathNotification = this.notifyRepository.getNotification();
      const blobData = await this.documentsService.getLetterPDF(deathNotification, 'engagement');
      if (blobData && blobData.length > 0) {
        this.data = blobData;
        this.status = 'attached';
        this.filename = 'Letter of Engagement.pdf';
        await this.generateThumbnail(blobData);
        this.letterLoading = false;
      } else {
        this.showMessage(this.dialog, 'Letter could not be generated','Ensure you have completed your Firm and User details<br/><br/>Click on your email address in the blue bar above to check.');
      }
    } catch (error) {
      this.logService.log(`Error initiating PDF report ${error}`);
      this.letterLoading = false;
      this.showErrorMessage(this.dialog, 'PDF report could not be initiated - please try again later');
    }
  
  }

  private loadFileData(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        resolve(result as string);
      };
      reader.onerror = event => {
        this.logService.error(`File could not be read! Code ${event.target}`);
        reject(event.target);
      };
      reader.readAsDataURL(file);
    });

  }

  private hasAllowedMimeType(data: string): boolean {
    return data.startsWith('data:image/') || data.startsWith('data:application/pdf');
  }
}
